import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaShoppingBag, FaTimesCircle, FaRedo, FaHeart, FaSpinner, FaCoffee } from 'react-icons/fa';
import { Order, OrderStatusLabels, OrderStatusColors } from '../../types/order';
import { formatOrderDateTime, formatPrice, getStatusIconElement } from '../../utils/orderUtils';
import { userOrdersApi } from '../../services/userOrdersApi';

interface UserOrderDetailModalProps {
  selectedOrder: Order | null;
  favorites: Set<number>;
  onClose: () => void;
  onReorder: (order: Order) => void;
  onFavoriteToggle: (orderId: number) => void;
}

const UserOrderDetailModal: React.FC<UserOrderDetailModalProps> = ({
  selectedOrder,
  favorites,
  onClose,
  onReorder,
  onFavoriteToggle,
}) => {
  const [orderDetail, setOrderDetail] = useState<Order | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadOrderDetail = useCallback(async () => {
    if (!selectedOrder) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const detail = await userOrdersApi.getOrderDetail(selectedOrder.id);
      setOrderDetail(detail);
    } catch (err) {
      setError('Sipariş detayları yüklenemedi');
      console.error('Order detail yükleme hatası:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedOrder]);

  // Modal açıldığında sipariş detayını yükle
  useEffect(() => {
    if (selectedOrder) {
      loadOrderDetail();
    }
  }, [selectedOrder, loadOrderDetail]);

  if (!selectedOrder) {
    return null;
  }

  const displayOrder = orderDetail || selectedOrder;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-gradient-to-r from-green-500 to-green-400 rounded-xl">
                  <FaShoppingBag className="text-white text-xl" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Sipariş #{selectedOrder.id}</h2>
                  <p className="text-gray-600">{formatOrderDateTime(selectedOrder.created_at)}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimesCircle className="text-xl" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Sipariş Durumu */}
              <div className="bg-gradient-to-r from-gray-50 to-white rounded-xl p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusIconElement(selectedOrder.status)}
                    <span className="font-medium text-gray-800">
                      {OrderStatusLabels[selectedOrder.status]}
                    </span>
                  </div>
                  <span
                    className={`px-4 py-2 rounded-full text-sm font-medium ${OrderStatusColors[selectedOrder.status]}`}
                  >
                    {OrderStatusLabels[selectedOrder.status]}
                  </span>
                </div>
              </div>

              {/* Sipariş Bilgileri */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-blue-50 rounded-xl p-4">
                  <h3 className="font-semibold text-blue-800 mb-2">Sipariş Bilgileri</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sipariş No:</span>
                      <span className="font-medium">#{selectedOrder.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tarih:</span>
                      <span className="font-medium">
                        {formatOrderDateTime(selectedOrder.created_at)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Toplam Tutar:</span>
                      <span className="font-bold text-green-600">
                        {formatPrice(selectedOrder.total_amount || 0)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 rounded-xl p-4">
                  <h3 className="font-semibold text-green-800 mb-2">Teslimat Bilgileri</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Birim:</span>
                      <span className="font-medium">{selectedOrder.management}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Ofis:</span>
                      <span className="font-medium">{selectedOrder.office}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Kullanılan Fiş:</span>
                      <span className="font-bold text-green-600">
                        {formatPrice(selectedOrder.ticket_used || 0)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Ürünler */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h3 className="font-semibold text-gray-800 mb-4">Sipariş Edilen Ürünler</h3>

                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <FaSpinner className="animate-spin text-2xl text-gray-400" />
                    <span className="ml-2 text-gray-600">Detaylar yükleniyor...</span>
                  </div>
                ) : error ? (
                  <div className="text-center py-8">
                    <p className="text-red-600 mb-2">{error}</p>
                    <button
                      onClick={loadOrderDetail}
                      className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      Tekrar Dene
                    </button>
                  </div>
                ) : displayOrder.items && displayOrder.items.length > 0 ? (
                  <div className="space-y-3">
                    {displayOrder.items.map(item => (
                      <div key={item.id} className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-gradient-to-r from-green-400 to-emerald-500 rounded-lg flex items-center justify-center">
                              <FaCoffee className="text-white text-sm" />
                            </div>
                            <div>
                              <h4 className="font-semibold text-gray-900">{item.name}</h4>

                              {/* Dynamic Options (Yeni Format) */}
                              {item.options && Object.keys(item.options).length > 0 && (
                                <div className="flex flex-wrap gap-1 mt-1">
                                  {Object.entries(item.options).map(([key, value], idx) => {
                                    // Renk paletleri
                                    const colors = [
                                      'bg-blue-100 text-blue-600',
                                      'bg-purple-100 text-purple-600',
                                      'bg-green-100 text-green-600',
                                      'bg-orange-100 text-orange-600',
                                    ];

                                    // Türkçe label mapping
                                    const labelMap: Record<string, string> = {
                                      boyut: 'Boyut',
                                      dem: 'Dem',
                                      seker: 'Şeker',
                                      sicaklik: 'Sıcaklık',
                                    };

                                    // Türkçe value mapping
                                    const valueMap: Record<string, string> = {
                                      normal: 'Normal',
                                      double: 'Büyük',
                                      acik: 'Açık',
                                      demli: 'Demli',
                                      sekersiz: 'Şekersiz',
                                      orta: 'Orta',
                                      sekerli: 'Şekerli',
                                      sicak: 'Sıcak',
                                      ilik: 'Ilık',
                                      soguk: 'Soğuk',
                                    };

                                    const displayLabel = labelMap[key] || key;
                                    const displayValue = valueMap[value as string] || String(value);

                                    return (
                                      <span
                                        key={key}
                                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${colors[idx % colors.length]}`}
                                      >
                                        {displayLabel}: {displayValue}
                                      </span>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Backward Compatibility - Eski Format */}
                              {!item.options && (item.size || item.strength) && (
                                <div className="flex gap-1 mt-1">
                                  {item.size && (
                                    <span className="px-2 py-0.5 bg-blue-100 text-blue-600 rounded-full text-xs font-medium">
                                      Boyut: {item.size === 'normal' ? 'Normal' : 'Büyük'}
                                    </span>
                                  )}
                                  {item.strength && (
                                    <span className="px-2 py-0.5 bg-purple-100 text-purple-600 rounded-full text-xs font-medium">
                                      Dem:{' '}
                                      {item.strength === 'light'
                                        ? 'Açık'
                                        : item.strength === 'normal'
                                          ? 'Normal'
                                          : 'Demli'}
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-green-600">{item.quantity} adet</p>
                            <p className="text-sm text-gray-600">
                              {formatPrice(item.price)} / adet
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-700 text-center py-4">
                    {selectedOrder.items_summary || 'Ürün detayları mevcut değil'}
                  </p>
                )}
              </div>

              {/* Aksiyon Butonları */}
              <div
                className={`flex items-center pt-4 ${
                  selectedOrder.status === 'completed' ? 'gap-4' : 'justify-end'
                }`}
              >
                {selectedOrder.status === 'completed' && (
                  <button
                    onClick={() => {
                      onReorder(selectedOrder);
                      onClose();
                    }}
                    className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-green-500 to-green-400 text-white rounded-xl font-medium shadow-lg hover:from-green-600 hover:to-green-500 transition-all"
                  >
                    <FaRedo />
                    Tekrar Sipariş Ver
                  </button>
                )}
                <button
                  onClick={() => onFavoriteToggle(selectedOrder.id)}
                  className={`relative px-6 py-3 rounded-xl font-medium transition-colors duration-200 ${
                    favorites.has(selectedOrder.id)
                      ? 'bg-red-500 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-red-50 hover:text-red-600 border border-gray-200'
                  }`}
                >
                  <div className="flex items-center justify-center gap-2">
                    <FaHeart className="transition-colors duration-200" />
                    <span className="font-semibold">
                      {favorites.has(selectedOrder.id) ? 'Favorilerimde' : 'Favorilere Ekle'}
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UserOrderDetailModal;
